import { useGitHubBranchProtectionSetup } from './useGitHubBranchProtectionSetup'
import { useEffect, useState, type ReactNode } from 'react'
import { getIntegrationProvider } from '@/pages/integrations/providers'
import { getIntegrationBySlug, getIntegrationByType } from '@deepguide-ai/types'
import type { IntegrationType } from '@deepguide-ai/types'
import { queryClient, trpc } from '@/lib/trpc'
import {
  CloudConnectionManagerDialog,
  isScreenataCloudProvider,
  useScreenataCloudConnectionManager,
  type CloudProvider,
} from '@/components/screenata/cloud-connections'
import { getCloudSlugForIntegrationType } from './integration-connect-constants'

/**
 * Resolve a connect identifier (registry slug or IntegrationType enum
 * value) to a cloud provider id, when applicable.
 */
function toCloudProvider(id: string): CloudProvider | null {
  if (isScreenataCloudProvider(id)) return id
  const cloudSlug = getCloudSlugForIntegrationType(id)
  if (cloudSlug) return cloudSlug
  const entry = getIntegrationByType(id) ?? getIntegrationBySlug(id)
  if (entry && isScreenataCloudProvider(entry.slug)) return entry.slug
  return null
}

/**
 * IntegrationTypes whose `openConnect` flow is fully self-contained:
 * cloud configs and OAuth-based providers that open their own popup.
 *
 * API-key providers (Okta, Auth0, JumpCloud, Datadog, etc.) call
 * `onSuccess` synchronously and rely on the caller's `onConnected`
 * to host the config modal — they work from IntegrationsPage but not
 * from generic surfaces like chat. Slack has no provider registered.
 *
 * Keep in lockstep with the server's `COMMONLY_USED_INTEGRATIONS`
 * (server/src/services/ai/tools/integrations.tool.ts).
 */
const SELF_CONTAINED_CONNECT_TYPES = new Set([
  'Aikido',
  'GitHub',
  'Slack',
  'Linear',
  'Asana',
  'ServiceNow',
  'Box',
  'Snowflake',
  'Zoom',
  'HubSpot',
  'Dropbox',
  'Monday',
  'Trello',
  'Wrike',
  'Salesforce',
  'ClickUp',
  'HiBob',
  'Checkr',
  'Jira',
  'MicrosoftTeams',
  'Bitbucket',
  'GitLab',
  'Confluence',
  'Notion',
  'GoogleDrive',
  'Zendesk',
  'Intercom',
  'Freshdesk',
  'AZURE_DEVOPS',
  'QuickBooks',
])

/** Returns true when `openConnect(id)` will actually open a flow.
 *  Accepts either a registry slug (e.g. 'github') or an
 *  IntegrationType enum value (e.g. 'GitHub'). Slugs are resolved via
 *  the registry; the resulting integrationType is matched
 *  case-insensitively against the self-contained set since the
 *  registry stores values in UPPER_SNAKE while the enum is TitleCase. */
export function canOpenConnect(id: string): boolean {
  if (toCloudProvider(id)) return true
  if (SELF_CONTAINED_CONNECT_TYPES.has(id)) return true
  const integrationType = getIntegrationBySlug(id)?.integrationType
  if (!integrationType) return false
  const itLower = integrationType.toLowerCase()
  for (const t of SELF_CONTAINED_CONNECT_TYPES) {
    if (t.toLowerCase() === itLower) return true
  }
  return false
}

/**
 * True when the provider has a registered ConfigModal we can host — i.e.
 * API-key providers (Okta, Google Workspace, Auth0, Datadog, …) whose
 * connect flow isn't self-contained and needs the caller to render the
 * credential modal. Unlike `canOpenConnect`, this returns true for those
 * providers, BUT the caller is responsible for mounting a config-modal
 * host (e.g. `useIntegrationConnectController().ConfigModalPortal`) and
 * routing the connect click to `setOpenConfigModal(integrationType)`.
 * Accepts a registry slug or an IntegrationType.
 */
export function hasConfigModalProvider(id: string): boolean {
  const integrationType = getIntegrationBySlug(id)?.integrationType ?? id
  return Boolean(getIntegrationProvider(integrationType as IntegrationType)?.ConfigModal)
}

export interface UseIntegrationConnectOptions {
  workspaceId: string
  /** Notified after a successful connect for any integration. */
  onConnected?: (id: string) => void
}

export function useIntegrationConnect({
  workspaceId,
  onConnected,
}: UseIntegrationConnectOptions) {
  const branchProtection = useGitHubBranchProtectionSetup(workspaceId)
  const [connectingFor, setConnectingFor] = useState<string | null>(null)

  const cloudManager = useScreenataCloudConnectionManager({
    // RFC-197 P2.2 — this manager drives the shared connect dialog; show the
    // post-connect Phase-D result instead of closing silently.
    showConnectResult: true,
    onConnected: (provider) => {
      setConnectingFor(null)
      const integrationType = getIntegrationBySlug(provider)?.integrationType
      onConnected?.(integrationType ?? provider)
    },
  })

  // Reset spinner for cloud connects when the user closes the dialog
  // (success path is already handled in onConnected above).
  useEffect(() => {
    if (cloudManager.configuringProviderId !== null) return
    setConnectingFor((prev) => (prev && toCloudProvider(prev) ? null : prev))
  }, [cloudManager.configuringProviderId])

  const openConnect = (id: string, connectionId?: string) => {
    const cloudSlug = toCloudProvider(id)
    if (cloudSlug) {
      setConnectingFor(id)
      cloudManager.setEditingConnectionId(connectionId ?? null)
      cloudManager.setActiveConnectionId(connectionId ?? null)
      cloudManager.clearConnectResult()
      cloudManager.setAzureShowArmSetup(false)
      cloudManager.setConfiguringProviderId(cloudSlug)
      return
    }

    // `id` may be a registry slug (e.g. 'github') or an IntegrationType
    // (e.g. 'GitHub'). Try direct lookup first, then resolve via the
    // self-contained set with case-insensitive matching since the
    // registry stores integrationType in UPPER_SNAKE while the
    // providers map is keyed by TitleCase IntegrationType values.
    const resolvedKey = (() => {
      if (getIntegrationProvider(id as IntegrationType))
        return id as IntegrationType
      const integrationType = getIntegrationBySlug(id)?.integrationType
      const candidate = integrationType?.toLowerCase()
      if (!candidate) return null
      for (const t of SELF_CONTAINED_CONNECT_TYPES) {
        if (t.toLowerCase() === candidate) return t as IntegrationType
      }
      return null
    })()
    const provider = resolvedKey
      ? getIntegrationProvider(resolvedKey)
      : undefined
    if (!provider) {
      console.warn(`No integration provider registered for ${id}`)
      return
    }

    setConnectingFor(id)
    void provider
      .handleConnect({
        workspaceId,
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
          })
          await queryClient.invalidateQueries({
            queryKey: trpc.integrations.getConnectedProviderKeys.queryKey(),
          })
          await queryClient.invalidateQueries({
            queryKey: trpc.integrations.listProviderConnections.queryKey(),
          })
          await queryClient.invalidateQueries({
            queryKey: trpc.integrations.getCapabilityStatuses.queryKey(),
          })
          // RFC-197 — surface the async evidence reconcile this connect just
          // enqueued; refetch so the "Syncing checks…" badge isn't missed
          // between idle polls.
          await queryClient.invalidateQueries({
            queryKey: trpc.integrations.evidenceReconcileStatus.queryKey(),
          })
          if (provider.id === 'GitHub') {
            await queryClient.invalidateQueries({
              queryKey: trpc.githubApp.listConnectedRepositories.queryKey({
                workspaceId,
              }),
            })
            await queryClient.invalidateQueries({
              queryKey: trpc.githubApp.listInstallations.queryKey(),
            })
          }
          setConnectingFor(null)
          if (provider.id === 'GitHub') await branchProtection.start()
          onConnected?.(id)
        },
        onFinish: (status) => {
          if (status !== 'success') setConnectingFor(null)
        },
      })
      .catch(() => setConnectingFor(null))
  }

  const DialogPortal: ReactNode = (
    <>
      <CloudConnectionManagerDialog cloudManager={cloudManager} />
      {branchProtection.dialog}
    </>
  )

  return {
    openConnect,
    connectingFor,
    DialogPortal,
    /** Exposed so consumers (e.g. CloudScanCard) can read connection state without spawning a second manager. */
    cloudManager,
  }
}
